import React, { useEffect, useRef } from "react";
import type { AgentNode, ProgressMessage } from "../types";

const SCHEMATIC_NODES: { id: AgentNode; label: string }[] = [
  { id: "architect", label: "Research" },
  { id: "enrichment", label: "Datasheets" },
  { id: "review_arch", label: "Proposal" },
  { id: "simulation", label: "Simulation" },
  { id: "schematic", label: "Schematic" },
  { id: "layout", label: "Layout" },
  { id: "routing", label: "Routing" },
  { id: "review", label: "Audit" },
  { id: "export", label: "Export" },
];

const PCB_NODES: { id: string; label: string }[] = [
  { id: "pcb_start", label: "Start Worker" },
  { id: "pcb_upload", label: "Upload Schematics" },
  { id: "pcb_footprint", label: "Footprints" },
  { id: "pcb_netlist", label: "Netlist Sync" },
  { id: "pcb_place", label: "Placement" },
  { id: "pcb_zone", label: "GND Zone" },
  { id: "pcb_diffpair", label: "Diff Pairs" },
  { id: "pcb_lock", label: "Lock Nets" },
  { id: "pcb_route", label: "Autoroute" },
  { id: "pcb_drc", label: "DRC" },
  { id: "pcb_gates", label: "Gates" },
  { id: "pcb_export", label: "Export" },
];

// Map PCB log messages to pipeline step IDs
// Handles 3-phase pipeline: setup (steps 1-8), route (step 9), finalize (steps 10-12)
function pcbStepFromMessage(msg: string): string | null {
  const lower = msg.toLowerCase();
  // Phase 1: Setup
  if (lower.includes("starting pcb worker") || lower.includes("worker ready") && !lower.includes("routing") && !lower.includes("finalize")) return "pcb_start";
  if (lower.includes("uploading schematic") || lower.includes("schematics uploaded") || lower.includes("downloading built")) return "pcb_upload";
  if (lower.includes("step 1") || lower.includes("assigning footprint") || lower.includes("assigned") && lower.includes("footprint")) return "pcb_footprint";
  if (lower.includes("step 2") || lower.includes("netlist") || lower.includes("building netlist")) return "pcb_netlist";
  if (lower.includes("step 3") || lower.includes("placing") || lower.includes("placed") && lower.includes("component")) return "pcb_place";
  if (lower.includes("step 4") || lower.includes("gnd zone") || lower.includes("zones added")) return "pcb_zone";
  if (lower.includes("step 5") || lower.includes("differential")) return "pcb_diffpair";
  if (lower.includes("step 6") || lower.includes("lock") || lower.includes("critical net") || lower.includes("placement complete")) return "pcb_lock";
  // Phase 2: Route
  if (lower.includes("freerouting") || lower.includes("autoroute") || lower.includes("routing worker") || lower.includes("routing complete") || lower.includes("running freerouting")) return "pcb_route";
  // Phase 3: Finalize
  if (lower.includes("drc") || lower.includes("running drc") || lower.includes("design settings")) return "pcb_drc";
  if (lower.includes("gate")) return "pcb_gates";
  if (lower.includes("gerber") || lower.includes("packaging") || lower.includes("pcb complete") || lower.includes("pipeline complete")) return "pcb_export";
  // Phase 2/3 worker ready should not reset to start
  if (lower.includes("loading placed board") || lower.includes("board loaded")) return "pcb_route";
  if (lower.includes("loading routed board") || lower.includes("applying design")) return "pcb_drc";
  return null;
}

// Map builder/planner messages to schematic pipeline stages
function schematicStepFromMessage(msg: string, node: string): AgentNode | null {
  const lower = msg.toLowerCase();
  
  // Planner stage messages -> architect/enrichment/review_arch
  if (node === "planner") {
    if (lower.includes("generating design plan") || lower.includes("extracting")) return "architect";
    if (lower.includes("enriching") || lower.includes("looking up") || lower.includes("enriched")) return "enrichment";
    if (lower.includes("design document") || lower.includes("hierarchy check") || lower.includes("validation")) return "review_arch";
    if (lower.includes("sheet") && lower.includes("components")) return "schematic";
    if (lower.includes("project ready") || lower.includes("plan complete")) return "review_arch";
    if (lower.includes("block diagram")) return "review_arch";
    return "architect";
  }
  
  // Builder stage messages -> schematic/layout/export
  if (node === "builder") {
    if (lower.includes("custom hierarchy") || lower.includes("re-resolving") || lower.includes("validation")) return "schematic";
    if (lower.includes("phase a") || lower.includes("initializing") || lower.includes("starting fargate")) return "schematic";
    if (lower.includes("resolving component") || lower.includes("rewrote")) return "schematic";
    if (lower.includes("schematic done") || lower.includes("schematic pdf")) return "schematic";
    if (lower.includes("layout") || lower.includes("pcb") || lower.includes("netlist")) return "layout";
    if (lower.includes("routing")) return "routing";
    if (lower.includes("erc") || lower.includes("audit")) return "review";
    if (lower.includes("packaging") || lower.includes("archive") || lower.includes("gerber") || lower.includes("build complete")) return "export";
    return "schematic";
  }
  
  // Export stage
  if (node === "export") {
    return "export";
  }
  
  return null;
}

export interface ProgressTrackerProps {
  currentNode: AgentNode | null;
  messages: ProgressMessage[];
  completed?: boolean;
  isPCBRunning?: boolean;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  currentNode,
  messages,
  completed = false,
  isPCBRunning = false,
}) => {
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [messages]);

  // Determine which pipeline to show
  const showPCB = isPCBRunning || messages.some(m => m.node === "pcb");
  const PIPELINE_NODES = showPCB ? PCB_NODES : SCHEMATIC_NODES;

  // For PCB pipeline, determine current step from messages (always advance, never go back)
  let pcbCurrentIdx = -1;
  let pcbCompleted = false;
  // For schematic pipeline, also derive from messages when node is planner/builder
  let schematicCurrentIdx = -1;
  let schematicCompleted = false;
  
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    
    // PCB pipeline detection
    if (showPCB && (msg.node === "pcb" || msg.node === "layout")) {
      const step = pcbStepFromMessage(msg.message);
      if (step) {
        const idx = PCB_NODES.findIndex(n => n.id === step);
        if (idx > pcbCurrentIdx) pcbCurrentIdx = idx;
      }
      if (msg.message.toLowerCase().includes("pcb complete") ||
          msg.message.toLowerCase().includes("pipeline complete") ||
          msg.message.toLowerCase().includes("packaging zip") ||
          (msg.message.toLowerCase().includes("complete") && msg.message.toLowerCase().includes("files"))) {
        pcbCompleted = true;
      }
    }
    
    // Schematic pipeline detection from planner/builder/export messages
    if (!showPCB && (msg.node === "planner" || msg.node === "builder" || msg.node === "export")) {
      const step = schematicStepFromMessage(msg.message, msg.node);
      if (step) {
        const idx = SCHEMATIC_NODES.findIndex(n => n.id === step);
        if (idx > schematicCurrentIdx) schematicCurrentIdx = idx;
      }
      if (msg.message.toLowerCase().includes("build complete") ||
          msg.message.toLowerCase().includes("archive ready")) {
        schematicCompleted = true;
      }
    }
  }

  // Use derived index if we got messages, otherwise fall back to currentNode
  let currentIdx: number;
  if (showPCB) {
    currentIdx = pcbCurrentIdx;
  } else if (schematicCurrentIdx >= 0) {
    currentIdx = schematicCurrentIdx;
  } else {
    currentIdx = currentNode ? PIPELINE_NODES.findIndex((n) => n.id === currentNode) : -1;
  }
  const totalSteps = PIPELINE_NODES.length;
  const completedSteps = currentIdx >= 0 ? currentIdx : 0;
  const pct = (completed && !showPCB) || pcbCompleted || schematicCompleted
    ? 100
    : currentIdx >= 0
      ? Math.round(((completedSteps + 0.5) / totalSteps) * 100)
      : 0;

  const nodeStatus = (nodeId: string, idx: number): "done" | "active" | "pending" => {
    if ((completed && !showPCB) || pcbCompleted || schematicCompleted) return "done";
    if (currentIdx < 0) return "pending";
    if (idx < currentIdx) return "done";
    if (idx === currentIdx) return "active";
    return "pending";
  };

  if (!currentNode && messages.length === 0) return null;

  return (
    <div className="progress-tracker" role="status" aria-live="polite">
      <h2>Pipeline Progress</h2>

      {/* Progress bar */}
      <div className="progress-bar-container">
        <div className="progress-bar" style={{ width: `${pct}%` }}>
          <span className="progress-bar-text">{pct}%</span>
        </div>
      </div>

      {/* Node steps */}
      <ol className="node-list">
        {PIPELINE_NODES.map((node, idx) => {
          const status = nodeStatus(node.id, idx);
          return (
            <li key={node.id} className={`node-item node-${status}`}>
              <span className="node-indicator" aria-hidden="true">
                {status === "done" ? "✓" : status === "active" ? "⟳" : "○"}
              </span>
              <span className="node-label">{node.label}</span>
            </li>
          );
        })}
      </ol>

      {/* Live activity log */}
      {messages.length > 0 && (
        <div className="progress-log" ref={logRef}>
          <h3>Activity Log ({messages.length})</h3>
          <ul>
            {messages.map((msg, i) => {
              // Extract sheet name from session_id like "proj-xxx-01_Power_Input"
              const sheetMatch = msg.session_id?.match(/-(\d{2}_[^-]+)$/);
              const sheetTag = sheetMatch ? `[${sheetMatch[1]}] ` : "";
              // Render checklist buttons for popup/PDF download
              if (msg.message === "__CHECKLIST_BUTTONS__") {
                return (
                  <li key={i} style={{listStyle:'none', padding:'8px 0'}}>
                    <button
                      onClick={() => {
                        const html = (window as any).__pcbChecklistHtml;
                        if (html) {
                          const w = window.open('', '_blank', 'width=850,height=650');
                          if (w) { w.document.write(html); w.document.close(); }
                        }
                      }}
                      style={{padding:'8px 16px',marginRight:'10px',cursor:'pointer',background:'#2196F3',color:'white',border:'none',borderRadius:'4px',fontSize:'14px'}}
                    >
                      🖨️ Open Checklist (Print/PDF)
                    </button>
                    <button
                      onClick={() => {
                        const html = (window as any).__pcbChecklistHtml;
                        if (html) {
                          const blob = new Blob([html], {type: 'text/html'});
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = 'pcb_cleanup_checklist.html';
                          document.body.appendChild(a); a.click(); document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }
                      }}
                      style={{padding:'8px 16px',cursor:'pointer',background:'#4CAF50',color:'white',border:'none',borderRadius:'4px',fontSize:'14px'}}
                    >
                      💾 Download Checklist (.html)
                    </button>
                  </li>
                );
              }
              return (
                <li key={i}>
                  <span className="log-node">[{msg.node}]</span> {sheetTag}{msg.message}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;
